import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import MemorizedSidebar from './components/sidebar/sidebar';
import LandingLogoIntro, {
  clearLandingLogoIntro,
  shouldPlayLandingLogoIntro,
} from './components/LandingLogoIntro/LandingLogoIntro';
import { syncPageScrollOnRouteChange } from './utils/pageScroll';
import { isAdminDiscoSessionReady } from './utils/adminCompany';
import { isStaleBackForwardCachePage } from './utils/authSessionEpoch';

/**
 * Shown only while a lazy page chunk downloads.
 * Same app background (no white flash). No spinner overlay — page Backdrop
 * takes over after mount, so the UI cannot sit behind a stuck loader.
 */
const RouteChunkFallback = () => (
  <div
    className="app-route-chunk-fallback"
    aria-busy="true"
    style={{
      flex: 1,
      minWidth: 0,
      minHeight: 'calc(100vh - 4.25rem)',
      position: 'relative',
      background: '#F3F6FB',
      pointerEvents: 'none',
    }}
  >
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        height: 3,
        width: '40%',
        background: 'linear-gradient(90deg, #1db6e8, #003971)',
        animation: 'eops-boot-bar 1.1s ease-in-out infinite',
        pointerEvents: 'none',
      }}
    />
  </div>
);

const useAuth = () => {
    const persistRehydrated = useSelector(
      (state) => state._persist?.rehydrated ?? false,
    );
    const { userId, token, code, roleId, selectedAdminCompanyCode } =
      useSelector((state) => state.auth);

    if (!persistRehydrated) {
      return { ready: false, allowed: false };
    }

    if (!userId || !token) {
      return { ready: true, allowed: false };
    }

    const allowed = isAdminDiscoSessionReady(
      code,
      roleId,
      selectedAdminCompanyCode,
    );

    return { ready: true, allowed };
};

const isLandingPath = (pathname = '') =>
  pathname === '/landingpage' || pathname.endsWith('/landingpage');

const INTRO_BODY_CLASS = 'eops-landing-intro-active';

const ProtectedRoutes = ({ handleNCClick }) => {
    const auth = useAuth();
    const location = useLocation();

    useEffect(() => {
      if (isStaleBackForwardCachePage()) {
        window.location.replace('/');
      }
    }, []);
    const isStandaloneBillView =
      location.pathname.includes('advance-payment-bill') ||
      location.pathname.includes('mrc-bill');

    const [showLogoIntro, setShowLogoIntro] = useState(
      () =>
        isLandingPath(location.pathname) &&
        (shouldPlayLandingLogoIntro() || Boolean(location.state?.playLogoIntro))
    );
    const [introKey, setIntroKey] = useState(0);
    const [trackedPath, setTrackedPath] = useState(location.pathname);
    const [trackedLocKey, setTrackedLocKey] = useState(location.key);

    // Hide sidebar eOps logo before paint while intro plays (avoids first-load icon flash)
    if (typeof document !== 'undefined') {
      if (showLogoIntro) {
        document.body.classList.add(INTRO_BODY_CLASS);
      } else {
        document.body.classList.remove(INTRO_BODY_CLASS);
      }
    }

    // Only on real navigation to landing (other screen → landing, or eOps / Home re-nav)
    if (
      location.pathname !== trackedPath ||
      location.key !== trackedLocKey
    ) {
      const wasLanding = isLandingPath(trackedPath);
      const nowLanding = isLandingPath(location.pathname);
      const pathChanged = location.pathname !== trackedPath;
      const keyChanged = location.key !== trackedLocKey;

      setTrackedPath(location.pathname);
      setTrackedLocKey(location.key);

      if (nowLanding && (pathChanged || (wasLanding && keyChanged))) {
        setShowLogoIntro(true);
        setIntroKey((key) => key + 1);
      } else if (!nowLanding) {
        setShowLogoIntro(false);
      }
    }

    useEffect(() => {
      if (showLogoIntro) {
        document.body.classList.add(INTRO_BODY_CLASS);
      } else {
        document.body.classList.remove(INTRO_BODY_CLASS);
      }
      return () => document.body.classList.remove(INTRO_BODY_CLASS);
    }, [showLogoIntro]);

    useEffect(() => {
      // Clear boot paint (login navy / app top bar) once shell mounts
      const html = document.documentElement;
      const rootEl = document.getElementById('root');
      html.removeAttribute('data-boot');
      document.body.style.backgroundColor = '';
      if (rootEl) rootEl.style.backgroundColor = '';
    }, []);

    useEffect(() => {
        syncPageScrollOnRouteChange();
        const retryId = window.setTimeout(syncPageScrollOnRouteChange, 150);
        return () => window.clearTimeout(retryId);
    }, [location.pathname, location.search, location.key]);

    const handleIntroComplete = useCallback(() => {
      clearLandingLogoIntro();
      setShowLogoIntro(false);
    }, []);

    const NCCallBack = (applicationId) => {
        handleNCClick(applicationId)
    }

    if (!auth.ready) {
        return (
          <div
            className="app-route-chunk-fallback"
            aria-busy="true"
            style={{ minHeight: '100vh', background: '#F3F6FB' }}
          />
        );
    }

    if (!auth.allowed) {
        return <Navigate to="/" replace state={{ from: location.pathname }} />;
    }

    if (isStandaloneBillView) {
        return (
            <Suspense fallback={<RouteChunkFallback />}>
              <Outlet />
            </Suspense>
        );
    }

    return (
        <div
            className="app-main-layout"
            style={{
                display: 'flex',
                alignItems: 'flex-start',
                width: '100%',
                flex: 1,
                minWidth: 0,
                minHeight: 0,
            }}
        >
            {showLogoIntro ? (
              <LandingLogoIntro key={introKey} onComplete={handleIntroComplete} />
            ) : null}
            <MemorizedSidebar handleCallBack={NCCallBack} />
            <Suspense fallback={<RouteChunkFallback />}>
              <Outlet />
            </Suspense>
        </div>
    )
}
export default ProtectedRoutes;
